import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Truck, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { getAllDestinatariDDT, createDestinatarioDDT, updateDestinatarioDDT, deleteDestinatarioDDT } from "@/services/localDatabase";

const DestinatariDDT = () => {
  const [destinatari, setDestinatari] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    via: "",
    comune: "",
    provincia: "",
    partita_iva: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data = await getAllDestinatariDDT();
      setDestinatari(data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditing(item);
      setFormData({
        nome: item.nome || "",
        via: item.via || "",
        comune: item.comune || "",
        provincia: item.provincia || "",
        partita_iva: item.partita_iva || ""
      });
    } else {
      setEditing(null);
      setFormData({
        nome: "",
        via: "",
        comune: "",
        provincia: "",
        partita_iva: ""
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setFormData({
      nome: "",
      via: "",
      comune: "",
      provincia: "",
      partita_iva: ""
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }

    try {
      if (editing) {
        await updateDestinatarioDDT(editing.id, formData);
        toast.success("Destinatario DDT aggiornato");
      } else {
        await createDestinatarioDDT(formData);
        toast.success("Destinatario DDT aggiunto");
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo destinatario DDT?")) return;
    try {
      await deleteDestinatarioDDT(id);
      toast.success("Destinatario DDT eliminato");
      fetchData();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nell'eliminazione");
    }
  };

  if (loading) {
    return <div className="p-6">Caricamento...</div>;
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Truck className="w-8 h-8 text-blue-500" />
          <h1 className="text-2xl font-bold font-barlow">Destinatari DDT</h1>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-blue-500 hover:bg-blue-600" data-testid="add-destinatario-ddt-btn">
          <Plus className="w-4 h-4 mr-2" /> Aggiungi
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Archivio Destinatari DDT ({destinatari.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {destinatari.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Nessun destinatario DDT. Aggiungine uno!</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Indirizzo</TableHead>
                  <TableHead>P.IVA</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {destinatari.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">{d.nome}</TableCell>
                    <TableCell>{d.via}, {d.comune} ({d.provincia})</TableCell>
                    <TableCell>{d.partita_iva}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(d)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(d.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica Destinatario DDT" : "Nuovo Destinatario DDT"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nome *</Label>
                <Input value={formData.nome} onChange={(e) => setFormData(p => ({ ...p, nome: e.target.value.toUpperCase() }))} className="mt-1" />
              </div>
              <div>
                <Label>Partita IVA</Label>
                <Input value={formData.partita_iva} onChange={(e) => setFormData(p => ({ ...p, partita_iva: e.target.value.toUpperCase() }))} placeholder="IT12345678901" className="mt-1" />
              </div>
              <div>
                <Label>Via</Label>
                <Input value={formData.via} onChange={(e) => setFormData(p => ({ ...p, via: e.target.value }))} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Comune</Label>
                  <Input value={formData.comune} onChange={(e) => setFormData(p => ({ ...p, comune: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label>Prov.</Label>
                  <Input value={formData.provincia} onChange={(e) => setFormData(p => ({ ...p, provincia: e.target.value.toUpperCase() }))} maxLength={2} className="mt-1" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}><X className="w-4 h-4 mr-2" />Annulla</Button>
              <Button type="submit" className="bg-blue-500 hover:bg-blue-600"><Save className="w-4 h-4 mr-2" />Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DestinatariDDT;
